import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DipRegistrationService} from '../dip-registration.service';
import {DipCustomerIdentity} from './models/dip-registration-customer-identity.model';
import {DipRegistrationPrimaryAccountModalComponent} from '../modals/dip-registration-primary-account-modal/dip-registration-primary-account-modal.component';
import {Router} from '@angular/router';
import {DipRegistrationAlertStore} from '../core/services/alert/dip-registration-alert-store.service';
import {DipRegistrationConfirmService} from '../core/services/dip-registration-confirm.service';
import {WebMetricsService} from '../core/services/web-metrics/web-metrics.service';
import {DipRegistrationContextService} from '../core/services/dip-registration-context.service';
import {DipRegistrationAlertState} from '../core/services/alert/dip-registartion-alert-state.model';
import {Observable} from 'rxjs';
import {format, subYears} from 'date-fns';
import {TranslateService} from '@ngx-translate/core';
import { DiprsSpaLoggerService } from '../core/services/logger/diprs-spa-logger.service';
import { DipRegistrationLoggingNames } from '../core/enum/dip-registration-logging-names.enum';
import { DipRegistrationNavigationService } from '../core/services/dip-registration-navigation.service';
import { LoggerEventEnum } from '../core/models/dip-registration-log-information.model';
import { IdleService } from '../shared/services/idle.service';
import { ModalService } from '../shared/components/modal/modal.service';
import { DipRegistrationCustomerIdentityService } from '../core/services/dip-registration-customer-identity.service';
import {getWebMetricsData} from '../core/services/web-metrics/web-metrics-data.map';

@Component({
    selector: 'dip-registration-customer-identification',
    templateUrl: './dip-registration-customer-identification.component.html',
    styleUrls: ['./dip-registration-customer-identification.component.scss'],
    standalone: false
})
export class DipRegistrationCustomerIdentificationComponent implements OnInit, OnDestroy {
  customerIdentificationForm: UntypedFormGroup;
  minDate;
  maxDate;
  hasError = false;
  phoneNumber: string;
  language: string;
  dipRegistrationAlertState$: Observable<DipRegistrationAlertState>;
  readonly namePattern = '^[a-zA-ZàâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ][a-zA-ZàâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ\'\\-\\. ]*$';
  readonly emailPattern = '^[0-9a-zA-Z@_.\\-]*$';
  readonly emailValidationPattern = /^([A-Za-z0-9_\-.+])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/;
  readonly postalCodeValidationPattern = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;

  postalCodeMaskOptions = {
    mask: 'a0a 0a0',
    lazy: true,
    overwrite: true
  };

  constructor(
    private webMetricsService: WebMetricsService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private modalService: ModalService,
    private dipRegistrationAlertStore: DipRegistrationAlertStore,
    private dipRegistrationService: DipRegistrationService,
    private dipRegistrationContextService: DipRegistrationContextService,
    private dipRegistrationConfirmService: DipRegistrationConfirmService,
    private dipRegistrationIdleService: IdleService,
    private translateService: TranslateService,
    private dipRegistrationNavigationService: DipRegistrationNavigationService,
    private spaLoggerService: DiprsSpaLoggerService,
    private dipRegistrationCustomerIdentityService: DipRegistrationCustomerIdentityService
  ) {
    this.dipRegistrationAlertState$ = dipRegistrationAlertStore.state$;
  }

  ngOnInit() {
    this.spaLoggerService.info(LoggerEventEnum.PAGE_ENTERED, DipRegistrationLoggingNames.CUSTOMER_IDENTIFICATION, { previousPageId: this.dipRegistrationNavigationService.getPreviousUrl() });
    this.dipRegistrationService.startKeepingSessionAlive();
    this.dipRegistrationIdleService.start(this.dipRegistrationContextService.getSessionWarningTimeout());
    this.dipRegistrationConfirmService.start();
    this.phoneNumber = this.dipRegistrationContextService.getPhoneNumber();
    this.dipRegistrationAlertState$.subscribe(async (alert) => {
      if (alert.isEnabled) {
        if (alert.messageKey === 'DIPRSRegistration.alert.principal.account.message') {
          this.webMetricsService.submitErrorAndWarningTag('secondaryPolicyHolderNotFoundTag');
        } else if (alert.messageKey === 'DIPRSRegistration.alert.not.match.found.message') {
          this.webMetricsService.submitErrorAndWarningTag('primaryPolicyHolderNotFoundTag');
        }
      }
    });
    if (this.dipRegistrationContextService.isAuthenticated()) {
      this.dipRegistrationAlertStore.updateAlertState({
        isEnabled: true,
        type: 'success',
        messageKey: 'DIPRSRegistration.customerIdentification.success.fill.out.success.banner',
        titleKey: 'DIPRSRegistration.customerIdentification.success.fill.out.success.banner.title'
      });
    }
    this.minDate = format(subYears(new Date(), 125), 'yyyy-MM-dd');
    this.maxDate = format(subYears(new Date(), 15), 'yyyy-MM-dd');

    this.language = this.dipRegistrationContextService.getLanguage();
    this.createForm();
  }

  createForm() {
    this.customerIdentificationForm = this.formBuilder.group({
      firstName: new UntypedFormControl('', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(30), Validators.pattern(this.namePattern)])),
      lastName: new UntypedFormControl('', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(30), Validators.pattern(this.namePattern)])),
      birthDate: new UntypedFormControl('', Validators.compose([Validators.required])),
      email: new UntypedFormControl('', Validators.compose([Validators.required, Validators.maxLength(50), Validators.email, Validators.pattern(this.emailValidationPattern)])),
      postalCode: new UntypedFormControl('', Validators.compose([Validators.required, Validators.pattern(this.postalCodeValidationPattern)]))
    });
  }

  getFormControl(name) {
    return this.customerIdentificationForm.get(name);
  }

  async submit() {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.CUSTOMER_IDENTIFICATION, { id: 'CONTINUE'});
    this.dipRegistrationAlertStore.resetAlertState();
    this.hasError = false;

    if (this.customerIdentificationForm.valid) {
      const customerIdentityData = new DipCustomerIdentity(
        this.customerIdentificationForm.get('firstName').value.trim(),
        this.customerIdentificationForm.get('lastName').value.trim(),
        this.customerIdentificationForm.get('birthDate').value,
        this.customerIdentificationForm.get('postalCode').value.replace(' ', ''),
        this.customerIdentificationForm.get('email').value
      );

      this.dipRegistrationCustomerIdentityService.create(customerIdentityData);

    } else {
      window.scrollTo(0, 0);
      this.validateAllFormFields(this.customerIdentificationForm);
      // Timeout hasError value change to make Jaws read again the aria-live and announce the alert
      setTimeout(() => {
        this.hasError = true;
      }, 1);
    }
  }

  validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  async showModal(button: HTMLButtonElement) {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.CUSTOMER_IDENTIFICATION, { id: 'ACCOUNT_HOLDER_POPUP'});
    this.webMetricsService.submit(getWebMetricsData('accountHolderPopupTag'));
    this.modalService.create<any, void>(
      DipRegistrationPrimaryAccountModalComponent,
      (containerInstance, instance) => {
        instance.close.subscribe(containerInstance.destroy);
        containerInstance.closeLabel = this.translateService.instant('DIPRSRegistration.modal.close.label');
        containerInstance.modalDialogClass = 'modal-md';
      },
      button
    ).toPromise();
  }

  async cancel(button: HTMLButtonElement) {
    this.spaLoggerService.info(LoggerEventEnum.USER_ACTION, DipRegistrationLoggingNames.CUSTOMER_IDENTIFICATION, { id: 'CANCEL'});
    await this.router.navigate(['overview'], this.dipRegistrationContextService.buildParams({
      needConfirmation: true,
      button
    }));
  }

  ngOnDestroy() {
    this.spaLoggerService.info(LoggerEventEnum.PAGE_EXITED, DipRegistrationLoggingNames.CUSTOMER_IDENTIFICATION, null);
    this.dipRegistrationService.stopKeepingSessionAlive();
  }
}
